import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GetalInstelling } from '@vip/shared';
import { api } from '../../lib/apiClient';

/**
 * De instellingen: taalmodel, prompts, taxonomie en de getalknoppen.
 *
 * Wat hier stond over accounts (de lijst, rollen toekennen, uitnodigen) is
 * verhuisd naar `features/personen/personen.api.ts`. Een account is sinds het
 * samenvoegen geen eigen ding meer maar een eigenschap van een persoon, en twee
 * plekken die aan dezelfde mensen zitten is er één te veel.
 */

export interface TaalmodelKeuze {
  versie: number;
  model: string;
  standaard: string;
  keuzes: ReadonlyArray<{ id: string; naam: string; omschrijving: string }>;
}

export function useTaalmodel() {
  return useQuery({
    queryKey: ['taalmodel'],
    queryFn: () => api.get<TaalmodelKeuze>('/admin/taalmodel'),
  });
}

export function useZetTaalmodel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (model: string) => api.post<{ model: string }>('/admin/taalmodel', { model }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taalmodel'] }),
  });
}

export interface PromptInstelling {
  prompt: string;
  isStandaard: boolean;
  standaard: string;
}

/**
 * Beide prompts werken hetzelfde; alleen het pad en de plaatshouder verschillen.
 *
 * De twee functies heten `useGebruik` en `useBewaar` en niet `gebruik`/`bewaar`: het
 * zijn hooks, en de regel dat hooks zo heten is er niet voor de sier. Zonder die naam
 * kan geen mens en geen linter zien dat je ze alleen uit een component mag aanroepen.
 */
function promptHooks(pad: string, sleutel: string) {
  const useGebruik = () =>
    useQuery({ queryKey: [sleutel], queryFn: () => api.get<PromptInstelling>(`/admin/${pad}`) });
  const useBewaar = () => {
    const qc = useQueryClient();
    return useMutation({
      mutationFn: (prompt: string) => api.post<{ ok: true }>(`/admin/${pad}`, { prompt }),
      onSuccess: () => qc.invalidateQueries({ queryKey: [sleutel] }),
    });
  };
  return { gebruik: useGebruik, bewaar: useBewaar };
}

const samenvatting = promptHooks('samenvatting-prompt', 'samenvatting-prompt');
const hulpvraag = promptHooks('hulpvraag-prompt', 'hulpvraag-prompt');

export const useHulpvraagPrompt = hulpvraag.gebruik;
export const useBewaarHulpvraagPrompt = hulpvraag.bewaar;

/** De definities komen letterlijk uit `GETAL_INSTELLINGEN` in shared. Hier
 *  stond een tweede, handgeschreven kopie van die vorm, en die liep meteen uit
 *  de pas toen er een veld bij kwam. */
export interface GetalInstellingen {
  definities: GetalInstelling[];
  waarden: Record<string, number>;
}

export function useInstellingen() {
  return useQuery({
    queryKey: ['instellingen'],
    queryFn: () => api.get<GetalInstellingen>('/admin/instellingen'),
  });
}

export function useBewaarInstellingen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (waarden: Record<string, number>) =>
      api.post<{ ok: true }>('/admin/instellingen', waarden),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['instellingen'] }),
  });
}

export const useSamenvattingPrompt = samenvatting.gebruik;

export const useBewaarSamenvattingPrompt = samenvatting.bewaar;

export function useWijzigWachtwoord() {
  return useMutation({
    mutationFn: (invoer: { huidig: string; nieuw: string }) =>
      api.post('/accounts/wachtwoord', invoer),
  });
}

/** Het antwoordadres onder uitgaande mail. Stond bij de uitnodigingen, omdat je
 *  dáár merkt dat antwoorden anders stuiteren. Het is alleen geen handeling maar
 *  een instelling, en hij geldt voor élke mail die de app stuurt. */
export function useContactEmail() {
  return useQuery({
    queryKey: ['contact-email'],
    queryFn: () => api.get<{ email: string }>('/admin/contact-email'),
  });
}

export function useZetContactEmail() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (email: string) => api.post<{ ok: true }>('/admin/contact-email', { email }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['contact-email'] }),
  });
}
